"use client"

import { useState } from "react"
import { HttpTypes } from "@medusajs/types"
import { generateReviews } from "@lib/util/generate-reviews"

type Props = {
  product: HttpTypes.StoreProduct
}

const tabs = ["Description", "Details", "Reviews"] as const
type Tab = typeof tabs[number]

export default function PetSelectProductTabs({ product }: Props) {
  const [active, setActive] = useState<Tab>("Description")

  return (
    <div>
      {/* Tab Headers */}
      <div className="flex border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActive(tab)}
            className={`px-6 py-4 text-sm font-semibold transition-all relative ${
              active === tab
                ? "text-[#0B7A75]"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab}
            {active === tab && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0B7A75]" />
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="py-8">
        {active === "Description" && (
          <div className="prose prose-gray max-w-none">
            {product.description ? (
              <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                {product.description}
              </p>
            ) : (
              <p className="text-gray-400 italic">No description available.</p>
            )}
            {(product.metadata?.chinese_description as string) && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {product.metadata?.chinese_description as string}
                </p>
              </div>
            )}
          </div>
        )}

        {active === "Details" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { label: "Material", value: product.material },
              { label: "Origin", value: product.origin_country },
              { label: "Type", value: product.type?.value },
              { label: "Weight", value: product.weight ? `${product.weight}g` : null },
              {
                label: "Dimensions",
                value: product.length && product.width && product.height
                  ? `${product.length}L × ${product.width}W × ${product.height}H`
                  : null,
              },
            ]
              .filter((f) => f.value)
              .map((field) => (
                <div key={field.label} className="flex justify-between py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-500">{field.label}</span>
                  <span className="text-sm text-gray-900">{field.value}</span>
                </div>
              ))}
            {[
              { label: "Material", value: product.material },
              { label: "Origin", value: product.origin_country },
              { label: "Type", value: product.type?.value },
            ].filter(f => f.value).length === 0 && (
              <p className="text-gray-400 italic col-span-2">No additional details available.</p>
            )}
          </div>
        )}

        {active === "Reviews" && (
          <ReviewsTabContent product={product} />
        )}
      </div>
    </div>
  )
}

function ReviewsTabContent({ product }: { product: HttpTypes.StoreProduct }) {
  const data = generateReviews(
    product.handle ?? "",
    product.metadata as Record<string, unknown> | null
  )

  if (!data || data.reviews.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400 text-lg mb-2">No reviews yet</p>
        <p className="text-gray-500 text-sm">Be the first to review this product!</p>
      </div>
    )
  }

  const displayReviews = data.reviews.slice(0, 10)

  return (
    <div>
      {/* Summary */}
      <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-100">
        <span className="text-3xl font-bold text-gray-900">{data.averageRating.toFixed(1)}</span>
        <div>
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <span key={star} className={`text-lg ${star <= Math.round(data.averageRating) ? "text-amber-400" : "text-gray-200"}`}>★</span>
            ))}
          </div>
          <span className="text-sm text-gray-500">{data.totalReviews} reviews</span>
        </div>
      </div>
      {/* Individual reviews */}
      <div className="space-y-4">
        {displayReviews.map((review, i) => (
          <div key={i} className="py-3 border-b border-gray-50 last:border-0">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-gray-900">{review.reviewerName}</span>
                {review.verified && (
                  <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">Verified</span>
                )}
              </div>
              <time className="text-xs text-gray-400">
                {new Date(review.date + "T00:00:00").toLocaleDateString("en-AU", { year: "numeric", month: "short", day: "numeric" })}
              </time>
            </div>
            <div className="flex gap-0.5 mb-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <span key={star} className={`text-sm ${star <= review.rating ? "text-amber-400" : "text-gray-200"}`}>★</span>
              ))}
            </div>
            {review.body && <p className="text-sm text-gray-600">{review.body}</p>}
          </div>
        ))}
      </div>
      {data.reviews.length > 10 && (
        <p className="text-sm text-gray-400 mt-4 text-center">Showing 10 of {data.totalReviews} reviews</p>
      )}
    </div>
  )
}
